import React from 'react';
import { useWizard } from '../../context/WizardContext';
import { BarChart2, FileText, ArrowRight, Lightbulb, Target, CheckCircle2 } from 'lucide-react';

const B0_Introduction = () => {
  const { nextStep } = useWizard();

  const benefits = [
    {
      icon: Target,
      title: 'Plan Before You Collect',
      description: 'Know exactly what questions to ask in your survey to get the data you need for your chosen analysis methods.'
    },
    {
      icon: CheckCircle2,
      title: 'Ensure Data Compatibility',
      description: 'Match your question types to analysis requirements - avoid collecting data that cannot be properly analyzed.'
    },
    {
      icon: Lightbulb,
      title: 'Save Time and Effort',
      description: 'Prevent the common mistake of collecting data only to discover it does not support your intended analysis.'
    }
  ];

  const analysisTypes = [
    {
      type: 'Qualitative',
      color: '#7c3aed',
      methods: ['Thematic Analysis', 'Content Analysis', 'Sentiment Analysis', 'Narrative Analysis']
    },
    {
      type: 'Quantitative',
      color: '#0891b2',
      methods: ['Descriptive Statistics', 'Inferential Tests', 'Correlation', 'Regression']
    }
  ];

  return (
    <div className="wizard-step">
      <div className="intro-header" style={{
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
          marginBottom: '1rem'
        }}>
          <BarChart2 size={32} color="white" />
        </div>
        <h2 style={{ marginBottom: '0.5rem' }}>Step 1: Introduction</h2>
        <p className="step-description" style={{ maxWidth: '600px', margin: '0 auto' }}>
          Plan your data analysis strategy before building your survey. This ensures you collect 
          exactly the right data for your research.
        </p>
      </div>

      <div className="info-box" style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        border: '1px solid #7dd3fc',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          margin: '0 0 1rem 0',
          color: '#0369a1'
        }}>
          <FileText size={20} />
          Why Plan Analysis First?
        </h3>
        <p style={{ margin: 0, lineHeight: 1.7, color: '#0c4a6e' }}>
          Many researchers build surveys first, then struggle to analyze the data because their questions 
          do not match analysis requirements. By planning your analysis approach first, you will know exactly 
          what types of questions to include - whether open-ended for thematic analysis, Likert scales for 
          statistical tests, or demographic categories for group comparisons.
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return (
            <div key={index} style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.75rem'
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: '#f0f9ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={18} color="#0891b2" />
                </div>
                <h4 style={{ margin: 0, fontSize: '1rem' }}>{benefit.title}</h4>
              </div>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: 1.6 }}>
                {benefit.description}
              </p>
            </div>
          );
        })}
      </div>

      <div style={{
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>What You Will Plan</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          {analysisTypes.map((type, index) => (
            <div key={index} style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1rem',
              borderLeft: `4px solid ${type.color}`
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: type.color }}>{type.type}</h4>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.9rem', color: '#64748b' }}>
                {type.methods.map((method, idx) => (
                  <li key={idx}>{method}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        background: '#fefce8',
        border: '1px solid #fde047',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <Lightbulb size={20} color="#ca8a04" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.9rem', color: '#713f12' }}>
          <strong>Integration with Survey Builder:</strong> Your analysis choices here will appear as helpful 
          reminders when you build your survey in Wizard F, guiding you to include the right question types.
        </div>
      </div>

      <div className="nav-buttons">
        <div></div>
        <button className="btn btn-primary" onClick={nextStep}>
          Get Started
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B0_Introduction;
